import { type FC, useEffect, useRef } from "react";
import type { SkillMenuItem } from "~/atoms/chatbot";

/** Stable ids so the textarea can point `aria-activedescendant` at the active
 *  option. Screen readers read `aria-activedescendant` from the focused
 *  element (the textarea), not from the listbox itself. */
export const SLASH_LISTBOX_ID = "skill-slash-listbox";
export const slashOptionId = (idx: number): string => `skill-slash-opt-${idx}`;

interface Props {
  /** Filtered + sorted skills to render. Empty → nothing rendered. */
  skills: SkillMenuItem[];
  /** Currently highlighted row. Out-of-range values are clamped. */
  activeIndex: number;
  /** Hover handler — caller updates `activeIndex`. */
  onHover: (index: number) => void;
  /** Click/Enter handler — caller resolves the skill. */
  onSelect: (skill: SkillMenuItem) => void;
}

/**
 * Floating autocomplete list rendered above the chatbot input.
 *
 * Visual language follows the existing chatbot conventions:
 *   - container = white card (`--indigo-canvas-highlight`)
 *     with `--indigo-border-default` and a soft drop shadow.
 *   - active row = `--indigo-gray-100`, matching the selected-conversation
 *     row in the chatbot sidebar (ChatbotModal:1432-1435) and the
 *     `.chatbot-suggestion-chip:hover` rule.
 *   - no accent color, no bold-on-active — selection is communicated
 *     by background alone, consistent with the rest of the chat surface.
 */
export const SkillSlashMenu: FC<Props> = ({
  skills,
  activeIndex,
  onHover,
  onSelect,
}) => {
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const clamped = Math.max(0, Math.min(activeIndex, skills.length - 1));

  // Keep the active row visible inside the scroll container when the user
  // navigates past the visible area with arrow keys.
  useEffect(() => {
    rowRefs.current[clamped]?.scrollIntoView({ block: "nearest" });
  }, [clamped]);

  // Early return must follow all hooks above (react-hooks/rules-of-hooks).
  if (skills.length === 0) return null;

  return (
    <div
      id={SLASH_LISTBOX_ID}
      role="listbox"
      aria-label="Skill commands"
      className="rounded-md border overflow-y-auto"
      style={{
        backgroundColor: "var(--indigo-canvas-highlight)",
        borderColor: "var(--indigo-border-default)",
        maxHeight: 220,
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
      }}
    >
      {skills.map((s, i) => {
        const isActive = i === clamped;
        return (
          <button
            key={s.id}
            id={slashOptionId(i)}
            ref={(el) => {
              rowRefs.current[i] = el;
            }}
            type="button"
            role="option"
            aria-selected={isActive}
            onMouseEnter={() => onHover(i)}
            // mousedown (not click) — the textarea's blur fires before click
            // and would tear down the menu before the select handler ran.
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(s);
            }}
            className="w-full text-left flex flex-col gap-0.5"
            style={{
              padding: "8px 12px",
              backgroundColor: isActive
                ? "var(--indigo-gray-100)"
                : "transparent",
              cursor: "pointer",
              transition: "background-color 120ms ease",
            }}
          >
            <span
              className="text-xs font-medium"
              style={{ color: "var(--indigo-text-primary)" }}
            >
              /{s.name}
            </span>
            <span
              className="text-[11px] truncate"
              style={{ color: "var(--indigo-text-tertiary)" }}
            >
              {firstLine(s.description)}
            </span>
          </button>
        );
      })}
      <div
        className="flex gap-3"
        style={{
          padding: "4px 12px",
          color: "var(--indigo-text-tertiary)",
          borderTop: "1px solid var(--indigo-border-subtle)",
          fontSize: 10,
        }}
      >
        <span>↑↓ navigate</span>
        <span>↩ / ⇥ select</span>
        <span>⎋ close</span>
      </div>
    </div>
  );
};

function firstLine(s: string): string {
  const idx = s.indexOf("\n");
  return idx === -1 ? s : s.slice(0, idx);
}
